/**
 * Multicast -- Switch > Multicast (REAL, site-scoped).
 *
 * Replaces the old FRR/PIM lab DEMO (deleted 2026-09-14: the owner opened this page and took its
 * demo router -- RP 10.99.0.1, vlan100/200/300 -- for production data). There is NO demo data
 * anywhere on this page: everything is the site's own IGMP-snooping intent and the LIVE state read
 * off its switches.
 *
 * WHAT THIS DOES: per site VLAN, turn IGMP SNOOPING on/off on the site's switches; optionally our
 * own querier (DEFAULT OFF -- leave it off when an upstream router/headend already queries the
 * VLAN, e.g. an IPTV feed, because a second querier conflicts), immediate-leave, an IGMP version,
 * and static multicast-router (uplink) ports. A Plan preview shows the EXACT ecCLI per switch and
 * how Meridian reaches it (direct / via gateway); Apply runs it one switch at a time. Below, a
 * live "What the switches see" section reads the switches back.
 *
 * WHAT THIS DOES NOT DO: no PIM, no gateway igmpproxy -- the upstream router (172.26.26.1 for the
 * IPTV VLAN) routes multicast between VLANs. Cross-VLAN delivery (MVR) is not built; the plan says
 * so. See services/site_multicast.py + services/switch_multicast.py.
 *
 * Design system: surface/ink tokens for structure; the status ramp (StatusPill) ONLY for
 * operational state; brand violet ONLY for interactive affordances (toggles, buttons, focus);
 * light + dark via tokens; tablet-first (cards, tables scroll inside their card). Writes are gated
 * to msp_admin -- a read-only operator sees the tables and the live state, but no controls.
 */
import { useState } from "react";
import { useSession } from "@/stores/session";
import {
  useApplyMulticast,
  useDeleteMulticastSetting,
  useMulticastIntent,
  useMulticastPlan,
  useMulticastState,
  useSaveMulticastSetting,
  type MulticastApplyResult,
  type MulticastPlanSwitch,
  type MulticastSetting,
  type MulticastSwitchState,
} from "@/lib/api/siteMulticast";
import { describeAccess, describeApplyOutcome } from "@/lib/switchApplyResult";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { RoleGate } from "@/components/auth/RoleGate";

// Writes (intent + apply) require an MSP administrator, matching the backend gate (is_admin).
const WRITE_ROLE = "msp_admin" as const;
const WRITE_REASON = "Changing multicast settings requires an MSP administrator.";
const QUERIER_HELP =
  "Leave OFF when an upstream router or headend already queries this VLAN (e.g. an IPTV feed). " +
  "A second querier conflicts with the provider's.";

const CARD = "rounded-lg border border-line bg-surface";
const INPUT =
  "rounded border border-line bg-surface px-2 py-1 text-sm text-ink outline-none focus:border-brand";

export default function Multicast() {
  const siteId = useSession((s) => s.currentSiteId);
  const siteName = useSession((s) => s.currentSite()?.name);

  // IGMP snooping is configured PER SITE (it deploys to a site's switches). No site = nothing here.
  if (!siteId) {
    return (
      <div>
        <PageHeader title="Multicast" subtitle="IGMP snooping on this site's switches" />
        <EmptyState
          title="Select a site"
          hint="Multicast (IGMP snooping) is configured per site and deploys to the site's switches. Pick a site from the scope bar."
        />
      </div>
    );
  }
  return <SiteMulticast siteId={siteId} siteName={siteName} />;
}

function SiteMulticast({ siteId, siteName }: { siteId: string; siteName?: string }) {
  const intent = useMulticastIntent(siteId);
  const settings = intent.data?.settings ?? [];
  const candidates = intent.data?.site_vlans ?? [];

  // Read the switches ON LOAD rather than behind a button. It is a `show`-only read, and the
  // question this page exists to answer -- what is actually on the switches -- cannot be
  // answered by a panel nobody opened. It takes a CLI session per switch, so the sections
  // render their own loading state instead of blocking the page.
  const live = useMulticastState(siteId, true);
  const liveSwitches = live.data?.state?.switches ?? [];

  // Every VLAN any switch reports, so the table below can show what Meridian does NOT manage.
  const discovered = new Map<number, { snooping: boolean | null; running: boolean | null }>();
  for (const sw of liveSwitches) {
    for (const v of sw.vlans ?? []) {
      const prev = discovered.get(v.vlan);
      // A VLAN is "on" at the site if ANY switch has it configured on.
      discovered.set(v.vlan, {
        snooping: v.snooping ?? prev?.snooping ?? null,
        running: v.running ?? prev?.running ?? null,
      });
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Multicast"
        subtitle={
          siteName
            ? `IGMP snooping on ${siteName}'s switches — real config, no demo data`
            : "IGMP snooping on this site's switches"
        }
      />

      {intent.error && <ErrorBanner error={intent.error} />}
      {intent.isLoading && <div className="text-sm text-ink-muted">Loading multicast settings…</div>}

      {intent.data && (
        <>
          <IntentSection
            siteId={siteId}
            settings={settings}
            candidates={candidates}
            discovered={discovered}
            discovering={live.isLoading}
          />
          <PlanSection siteId={siteId} hasIntent={settings.some((s) => s.snooping)} />
          <LiveStateSection
            switches={liveSwitches}
            isLoading={live.isLoading}
            isFetching={live.isFetching}
            error={live.error}
            onRefresh={() => void live.refetch()}
          />
        </>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------------------------
 * INTENT -- the per-VLAN snooping table + add/remove.
 * ------------------------------------------------------------------------------------------- */
function IntentSection({
  siteId,
  settings,
  candidates,
  discovered,
  discovering,
}: {
  siteId: string;
  settings: MulticastSetting[];
  candidates: { vlan_id: number; vlan_name: string }[];
  /** Every VLAN the switches actually report, with their live snooping state. */
  discovered: Map<number, { snooping: boolean | null; running: boolean | null }>;
  discovering: boolean;
}) {
  const canWrite = useSession((s) => s.hasRole(WRITE_ROLE));
  const configured = new Set(settings.map((s) => s.vlan_id));
  const unconfigured = candidates.filter((c) => !configured.has(c.vlan_id));

  // VLANs that exist on the switches but that Meridian has no row for. This is the gap the
  // page was hiding: nine VLANs on the hardware, three in the table, and the two carrying
  // multicast were among the six missing.
  const unmanaged = [...discovered.entries()]
    .filter(([vlan]) => !configured.has(vlan))
    .sort((a, b) => a[0] - b[0]);

  return (
    <section className={`${CARD} overflow-hidden`}>
      <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-ink">Snooping by VLAN</h2>
          <p className="mt-0.5 text-xs text-ink-muted">
            IGMP snooping keeps a multicast stream off ports with no receiver on that VLAN.
          </p>
        </div>
        <RoleGate min={WRITE_ROLE} mode="hide">
          <AddVlanControl siteId={siteId} unconfigured={unconfigured} />
        </RoleGate>
      </header>

      {settings.length === 0 ? (
        <div className="p-4">
          <EmptyState
            title="No VLANs snooped yet"
            hint={
              canWrite
                ? "Add a VLAN above to snoop IGMP on it. For an IPTV feed, snoop the media VLAN and leave the querier OFF — the headend already queries it."
                : "No VLAN at this site has IGMP snooping configured."
            }
          />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-ink-subtle">
              <tr className="[&>th]:px-4 [&>th]:py-2 [&>th]:font-medium">
                <th>VLAN</th>
                <th>Snooping</th>
                <th>Querier</th>
                <th>Immediate leave</th>
                <th>Version</th>
                <th>Mrouter ports</th>
                {canWrite && <th></th>}
              </tr>
            </thead>
            <tbody>
              {settings.map((row) => (
                <IntentRow key={row.vlan_id} siteId={siteId} row={row} canWrite={canWrite} />
              ))}
              {/* VLANs that exist on the SWITCHES but that Meridian has no row for. The page
                  used to list three while the hardware had nine, and the two actually
                  carrying multicast were among the six it never mentioned. */}
              {unmanaged.map(([vlan, live]) => (
                <UnmanagedRow
                  key={`unmanaged-${vlan}`}
                  siteId={siteId}
                  vlan={vlan}
                  live={live}
                  canWrite={canWrite}
                />
              ))}
            </tbody>
          </table>
          {discovering && (
            <p className="px-4 py-2 text-xs text-ink-muted">Reading the switches…</p>
          )}
          {!discovering && unmanaged.length > 0 && (
            <p className="px-4 py-2 text-xs text-ink-muted">
              {unmanaged.length} VLAN{unmanaged.length === 1 ? "" : "s"} below exist on the
              switches but are not managed here. Meridian will not change them until you add
              them.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * A VLAN the switches report that Meridian has no intent row for.
 *
 * Shown READ-ONLY with its live state and an explicit "Manage" action, rather than silently
 * adopted: taking over a VLAN someone configured by hand is a decision, and the next Apply
 * would push Meridian's idea of it to the switch.
 */
function UnmanagedRow({
  siteId,
  vlan,
  live,
  canWrite,
}: {
  siteId: string;
  vlan: number;
  live: { snooping: boolean | null; running: boolean | null };
  canWrite: boolean;
}) {
  const save = useSaveMulticastSetting(siteId);
  return (
    <tr className="[&>td]:px-4 [&>td]:py-3 border-t border-line">
      <td>
        <span className="font-mono text-ink">{vlan}</span>
        <span className="ml-2 rounded border border-line px-1.5 py-px text-[0.625rem] uppercase tracking-wide text-ink-subtle">
          on switch
        </span>
      </td>
      <td className="text-xs text-ink-muted">
        {live.snooping == null ? "—" : live.snooping ? "on" : "off"}
        {live.snooping && live.running === false && (
          <span className="ml-1 text-warn">(inactive)</span>
        )}
      </td>
      <td className="text-xs text-ink-subtle">—</td>
      <td className="text-xs text-ink-subtle">—</td>
      <td className="text-xs text-ink-subtle">—</td>
      <td className="text-xs text-ink-subtle">—</td>
      {canWrite && (
        <td className="text-right">
          <button
            type="button"
            disabled={save.isPending}
            onClick={() => save.mutate({ vlan_id: vlan, snooping: !!live.snooping })}
            className="text-xs font-medium text-brand hover:underline disabled:opacity-50"
          >
            {save.isPending ? "Adding…" : "Manage"}
          </button>
        </td>
      )}
    </tr>
  );
}

function AddVlanControl({
  siteId,
  unconfigured,
}: {
  siteId: string;
  unconfigured: { vlan_id: number; vlan_name: string }[];
}) {
  const [vlan, setVlan] = useState("");
  const save = useSaveMulticastSetting(siteId);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const vid = parseInt(vlan, 10);
    if (!Number.isInteger(vid) || vid < 1 || vid > 4094) return;
    save.mutate({ vlan_id: vid, snooping: true }, { onSuccess: () => setVlan("") });
  };

  return (
    <form onSubmit={submit} className="flex items-center gap-2">
      <input
        list="mcast-vlan-options"
        value={vlan}
        onChange={(e) => setVlan(e.target.value)}
        placeholder="VLAN id"
        inputMode="numeric"
        className={`${INPUT} w-28`}
        aria-label="VLAN id to snoop"
      />
      <datalist id="mcast-vlan-options">
        {unconfigured.map((c) => (
          <option key={c.vlan_id} value={c.vlan_id}>
            {c.vlan_name}
          </option>
        ))}
      </datalist>
      <Button variant="primary" type="submit" disabled={save.isPending || !vlan.trim()}>
        {save.isPending ? "Adding…" : "Add VLAN"}
      </Button>
    </form>
  );
}

function IntentRow({
  siteId,
  row,
  canWrite,
}: {
  siteId: string;
  row: MulticastSetting;
  canWrite: boolean;
}) {
  const save = useSaveMulticastSetting(siteId);
  const del = useDeleteMulticastSetting(siteId);
  const patch = (body: Partial<MulticastSetting>) =>
    save.mutate({ vlan_id: row.vlan_id, ...body });

  return (
    <tr className="border-t border-line/60 [&>td]:px-4 [&>td]:py-2 align-middle">
      <td>
        <span className="font-mono text-ink">{row.vlan_id}</span>
        {row.vlan_name && <span className="ml-2 text-xs text-ink-muted">{row.vlan_name}</span>}
      </td>
      <td>
        <Toggle
          on={row.snooping}
          disabled={!canWrite || save.isPending}
          onChange={(v) => patch({ snooping: v })}
          label={`IGMP snooping on VLAN ${row.vlan_id}`}
        />
      </td>
      <td>
        <div className="flex items-center gap-1.5">
          <Toggle
            on={row.querier}
            disabled={!canWrite || save.isPending}
            onChange={(v) => patch({ querier: v })}
            label={`Our IGMP querier on VLAN ${row.vlan_id}`}
          />
          <span className="text-ink-subtle" title={QUERIER_HELP} aria-label={QUERIER_HELP}>
            {/* help affordance; the tooltip carries the "leave off for IPTV" guidance */}
            ⓘ
          </span>
        </div>
      </td>
      <td>
        <Toggle
          on={row.immediate_leave}
          disabled={!canWrite || save.isPending}
          onChange={(v) => patch({ immediate_leave: v })}
          label={`Immediate leave on VLAN ${row.vlan_id}`}
        />
      </td>
      <td>
        {canWrite ? (
          <select
            className={INPUT}
            value={row.version ?? ""}
            onChange={(e) => patch({ version: e.target.value ? Number(e.target.value) : null })}
            aria-label={`IGMP version on VLAN ${row.vlan_id}`}
          >
            <option value="">default</option>
            <option value="2">v2</option>
            <option value="3">v3</option>
          </select>
        ) : (
          <span className="text-ink-muted">{row.version ? `v${row.version}` : "default"}</span>
        )}
      </td>
      <td>
        {canWrite ? (
          <MrouterPortsInput
            value={row.mrouter_ports}
            onSave={(ports) => patch({ mrouter_ports: ports })}
          />
        ) : (
          <span className="font-mono text-xs text-ink-muted">
            {row.mrouter_ports.length ? row.mrouter_ports.join(", ") : "—"}
          </span>
        )}
      </td>
      {canWrite && (
        <td className="text-right">
          <Button
            variant="ghost"
            disabled={del.isPending}
            onClick={() => del.mutate(row.vlan_id)}
            title="Forget this VLAN's multicast settings"
          >
            Remove
          </Button>
        </td>
      )}
    </tr>
  );
}

/** A comma-separated port list with an explicit Save (so a half-typed list is never sent). */
function MrouterPortsInput({
  value,
  onSave,
}: {
  value: string[];
  onSave: (ports: string[]) => void;
}) {
  const [text, setText] = useState(value.join(", "));
  const dirty = text.trim() !== value.join(", ");
  const commit = () => {
    const ports = text
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
    onSave(ports);
  };
  return (
    <span className="flex items-center gap-1.5">
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Ethernet6, …"
        className={`${INPUT} w-40 font-mono`}
        aria-label="Static multicast-router ports"
      />
      {dirty && (
        <Button variant="secondary" onClick={commit}>
          Save
        </Button>
      )}
    </span>
  );
}

/** A compact on/off switch. Brand violet when ON (an interactive affordance), never a status color. */
function Toggle({
  on,
  onChange,
  disabled,
  label,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
        on ? "bg-brand" : "bg-surface-2 border border-line"
      } ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
          on ? "translate-x-4" : "translate-x-1"
        }`}
      />
    </button>
  );
}

/* ---------------------------------------------------------------------------------------------
 * PLAN + APPLY -- the exact ecCLI per switch, then apply one switch at a time.
 * ------------------------------------------------------------------------------------------- */
function PlanSection({ siteId, hasIntent }: { siteId: string; hasIntent: boolean }) {
  const [open, setOpen] = useState(false);
  const plan = useMulticastPlan(siteId, open);
  const apply = useApplyMulticast(siteId);
  const [applyResult, setApplyResult] = useState<MulticastApplyResult | null>(null);
  const canWrite = useSession((s) => s.hasRole(WRITE_ROLE));

  return (
    <section className={`${CARD} overflow-hidden`}>
      <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-ink">Deploy plan</h2>
          <p className="mt-0.5 text-xs text-ink-muted">
            The exact switch commands and how Meridian reaches each switch. Review before applying.
          </p>
        </div>
        <RoleGate min={WRITE_ROLE} mode="disable" reason={WRITE_REASON}>
          <Button
            variant="secondary"
            disabled={!hasIntent}
            onClick={() => {
              setApplyResult(null);
              setOpen((v) => !v);
            }}
            title={hasIntent ? undefined : "Turn snooping on for a VLAN first"}
          >
            {open ? "Hide plan" : "Preview commands"}
          </Button>
        </RoleGate>
      </header>

      {open && (
        <div className="flex flex-col gap-4 p-4">
          {plan.isLoading && <div className="text-sm text-ink-muted">Planning…</div>}
          {plan.error && <ErrorBanner error={plan.error} />}

          {plan.data && (
            <>
              <UnverifiedBanner />
              {plan.data.refusals.length > 0 && (
                <ItemList tone="critical" title="Blocking issues" items={plan.data.refusals} />
              )}
              {plan.data.warnings.length > 0 && (
                <ItemList tone="warn" title="Warnings" items={plan.data.warnings} />
              )}

              {plan.data.switches.map((sw) => (
                <PlanSwitchCard key={sw.serial} sw={sw} result={applyResult} />
              ))}

              {canWrite && (
                <div className="flex items-center justify-end gap-3">
                  {!plan.data.can_apply && (
                    <span className="text-xs text-critical">
                      Resolve the blocking issues above before applying.
                    </span>
                  )}
                  <Button
                    variant="primary"
                    disabled={!plan.data.can_apply || apply.isPending}
                    onClick={() =>
                      apply.mutate(plan.data!.plan_token, { onSuccess: setApplyResult })
                    }
                  >
                    {apply.isPending ? "Applying…" : "Apply to switches"}
                  </Button>
                </div>
              )}
              {apply.error && <ErrorBanner error={apply.error} />}
              {applyResult && (
                <div className="text-sm text-ink">
                  {applyResult.ok
                    ? `Applied to ${applyResult.applied.length} switch(es).`
                    : `Stopped at ${applyResult.failed}. Nothing after it was attempted.`}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

function PlanSwitchCard({
  sw,
  result,
}: {
  sw: MulticastPlanSwitch;
  result: MulticastApplyResult | null;
}) {
  const applied = result?.switches.find((s) => s.serial === sw.serial);
  const access = describeAccess(sw.access) || sw.reachability || "";
  return (
    <div className={`${CARD} overflow-hidden`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-3 py-2">
        <div className="flex items-center gap-2">
          <StatusPill status={sw.online ? "healthy" : "critical"}>
            {sw.online ? "Online" : "Offline"}
          </StatusPill>
          <span className="text-sm font-medium text-ink">{sw.name}</span>
          <span className="font-mono text-xs text-ink-subtle">{sw.serial}</span>
        </div>
        {access && <span className="text-xs text-ink-muted">{access}</span>}
      </div>

      {sw.refusals.length > 0 && (
        <div className="px-3 pt-2">
          <ItemList tone="critical" items={sw.refusals} />
        </div>
      )}

      {sw.commands.length > 0 ? (
        <pre className="overflow-x-auto px-3 py-2 font-mono text-xs leading-relaxed text-ink">
          {sw.commands.join("\n")}
        </pre>
      ) : (
        <div className="px-3 py-2 text-xs text-ink-muted">Nothing to send to this switch.</div>
      )}

      {applied && (
        <div className="border-t border-line px-3 py-2">
          <ApplyOutcome applied={applied} />
        </div>
      )}
    </div>
  );
}

/** Reuse lib/switchApplyResult wording for a real (saved) apply of one switch. */
function ApplyOutcome({ applied }: { applied: MulticastApplyResult["switches"][number] }) {
  const msg = describeApplyOutcome(
    { ...applied, dry_run: false, error: applied.error ?? null },
    { saveRequested: true },
  );
  return (
    <div>
      <StatusPill status={msg.tone}>{msg.title}</StatusPill>
      {msg.detail && <p className="mt-1 text-xs text-ink-muted">{msg.detail}</p>}
    </div>
  );
}

/* ---------------------------------------------------------------------------------------------
 * LIVE STATE -- what the switches actually report (real; opens read-only `show` sessions).
 * ------------------------------------------------------------------------------------------- */
function LiveStateSection({
  switches,
  isLoading,
  isFetching,
  error,
  onRefresh,
}: {
  switches: MulticastSwitchState[];
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  onRefresh: () => void;
}) {

  return (
    <section className={`${CARD} overflow-hidden`}>
      <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-ink">What the switches see</h2>
          <p className="mt-0.5 text-xs text-ink-muted">
            Read straight off the switches: interfaces, routes, PIM and the streams flowing now.
          </p>
        </div>
        <Button variant="secondary" onClick={onRefresh} disabled={isFetching}>
          {isFetching ? "Reading…" : "Refresh"}
        </Button>
      </header>

      <div className="flex flex-col gap-4 p-4">
        {isLoading && <div className="text-sm text-ink-muted">Reading switches…</div>}
        {!!error && <ErrorBanner error={error as Error} />}
        {!isLoading && !error && switches.length === 0 && (
          <EmptyState title="No switches at this site" />
        )}
        {switches.map((sw) => (
          <LiveSwitchCard key={sw.serial} sw={sw} />
        ))}
      </div>
    </section>
  );
}

function LiveSwitchCard({ sw }: { sw: MulticastSwitchState }) {
  const reachable = sw.status === "observed" || sw.status === "not_observed";
  return (
    <div className={`${CARD} overflow-hidden`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-3 py-2">
        <span className="text-sm font-medium text-ink">{sw.name}</span>
        <div className="flex items-center gap-2 text-xs">
          {sw.global_snooping != null && (
            <StatusPill status={sw.global_snooping ? "healthy" : "warn"}>
              Snooping {sw.global_snooping ? "on" : "off"}
            </StatusPill>
          )}
          <span className="font-mono text-ink-subtle">{sw.serial}</span>
        </div>
      </div>

      {!reachable ? (
        <div className="px-3 py-2 text-xs text-critical">
          Could not read this switch{sw.error ? `: ${sw.error}` : "."}
        </div>
      ) : (
        <div className="flex flex-col gap-3 px-3 py-2">
          {sw.status === "not_observed" && (
            <p className="text-xs text-ink-muted">
              Reached the switch, but could not parse its IGMP output (the show-command format is
              unverified on this hardware). The commands ran; the read-back could not be read.
            </p>
          )}
          {sw.vlans && sw.vlans.length > 0 && (
            <table className="w-full text-left text-xs">
              <thead className="text-ink-subtle">
                <tr className="[&>th]:pr-4 [&>th]:py-1 [&>th]:font-medium">
                  <th>VLAN</th>
                  <th>Snooping</th>
                  <th>Running</th>
                  <th>Querier</th>
                  <th>Version</th>
                </tr>
              </thead>
              <tbody>
                {sw.vlans.map((v) => (
                  <tr key={v.vlan} className="[&>td]:pr-4 [&>td]:py-1">
                    <td className="font-mono text-ink">{v.vlan}</td>
                    <td className="text-ink-muted">{fmtBool(v.snooping)}</td>
                    {/* CONFIGURED and RUNNING are different facts. A VLAN can be enabled and
                        still inactive, and showing only the first calls it "on". */}
                    <td className={v.snooping && v.running === false ? "text-warn" : "text-ink-muted"}>
                      {v.running == null ? "—" : v.running ? "active" : "inactive"}
                    </td>
                    <td className="text-ink-muted">{v.querier == null ? "—" : v.querier ? "this switch" : "other"}</td>
                    <td className="text-ink-muted">{v.version ? `v${v.version}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {sw.groups && sw.groups.length > 0 && (
            <div>
              <div className="mb-1 text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-subtle">
                Active groups
              </div>
              <div className="flex flex-wrap gap-1.5">
                {sw.groups.map((g, i) => (
                  <span
                    key={i}
                    className="rounded border border-line px-1.5 py-px font-mono text-xs text-ink-muted"
                  >
                    {g.group} · vlan {g.vlan}
                  </span>
                ))}
              </div>
            </div>
          )}
          {sw.mrouter && sw.mrouter.length > 0 && (
            <div className="text-xs text-ink-muted">
              Mrouter ports:{" "}
              {sw.mrouter.map((m) => `${m.port} (vlan ${m.vlan}, ${m.kind})`).join(", ")}
            </div>
          )}

          <SwitchL3Sections sw={sw} />
        </div>
      )}
    </div>
  );
}

/**
 * The switch's L3 and multicast-ROUTING state: SVIs, routes, PIM, and what is actually
 * flowing.
 *
 * Until 2026-09-25 none of this reached the screen. The page showed three IGMP-snooping rows
 * for VLANs that were Link Down, while the same switch ran a PIM-SM router with 39 live
 * forwarding entries and a week of uptime.
 *
 * PIM IS GREYED OUT, NOT HIDDEN, on a switch that cannot do it. An absent control teaches
 * nothing; a disabled one with a reason teaches the shape of the estate. `capability.pim` is
 * PROBED -- it is false only when the switch rejected the command outright, which is a
 * different fact from PIM being switched off.
 */
function SwitchL3Sections({ sw }: { sw: MulticastSwitchState }) {
  const cap = sw.capability;
  const svis = sw.interfaces ?? [];
  const routes = sw.routes ?? [];
  const pim = sw.pim;
  const mroute = sw.mroute;

  // An older controller sends none of this; render nothing rather than a row of empty cards.
  if (!cap && !svis.length && !routes.length && !pim && !mroute) return null;

  // A switch that cannot route multicast gets ONE line, not three tables. Its interfaces and
  // routes are real, but they cannot matter on a multicast page, and showing them pushes the
  // switch that actually carries the streams below the fold. The reason is still stated, so
  // nobody has to wonder whether the read failed.
  if (cap && !cap.pim) {
    return (
      <div className="flex flex-col gap-1 border-t border-line pt-3">
        <div className="flex items-center gap-2">
          <span className="text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-subtle">
            Multicast routing (PIM)
          </span>
          <span className="rounded border border-line px-1.5 py-px text-[0.625rem] uppercase tracking-wide text-ink-subtle">
            not supported
          </span>
        </div>
        <p className="text-xs text-ink-subtle">
          Layer 2 only — this switch snoops multicast but cannot route it.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 border-t border-line pt-3">
      {/* ---- routed interfaces ---- */}
      <Block title="Routed interfaces" count={svis.length}>
        {svis.length === 0 ? (
          <p className="text-xs text-ink-muted">
            No routed interfaces — this switch is layer 2 only.
          </p>
        ) : (
          <table className="w-full text-left text-xs">
            <thead className="text-ink-subtle">
              <tr className="[&>th]:py-1 [&>th]:pr-4 [&>th]:font-medium">
                <th>VLAN</th>
                <th>Address</th>
                <th>Mode</th>
                <th>Link</th>
              </tr>
            </thead>
            <tbody>
              {svis.map((i) => (
                <tr key={i.vlan} className="[&>td]:py-1 [&>td]:pr-4">
                  <td className="font-mono text-ink">{i.vlan}</td>
                  <td className="font-mono text-ink-muted">
                    {i.ip ? `${i.ip}${i.prefix != null ? `/${i.prefix}` : ""}` : "—"}
                  </td>
                  <td className="text-ink-muted">{i.address_mode ?? "—"}</td>
                  {/* Admin-up with the link down is the state that made three dead VLANs
                      look like the whole multicast story. */}
                  <td className={i.link_up ? "text-ink-muted" : "text-warn"}>
                    {i.link_up ? "up" : i.admin_up ? "down" : "admin down"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Block>

      {/* ---- routes ---- */}
      {routes.length > 0 && (
        <Block title="Routes" count={routes.length}>
          <table className="w-full text-left text-xs">
            <thead className="text-ink-subtle">
              <tr className="[&>th]:py-1 [&>th]:pr-4 [&>th]:font-medium">
                <th>Destination</th>
                <th>Via</th>
                <th>Interface</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {routes.map((r, idx) => (
                <tr key={`${r.prefix}-${idx}`} className="[&>td]:py-1 [&>td]:pr-4">
                  <td className="font-mono text-ink">
                    {r.prefix}
                    {/* Two default routes at different distances is a real configuration
                        here; without this the screen cannot say which one is in use. */}
                    {r.selected && (
                      <span className="ml-1 text-[0.625rem] uppercase tracking-wide text-healthy">
                        in use
                      </span>
                    )}
                  </td>
                  <td className="font-mono text-ink-muted">{r.via ?? "connected"}</td>
                  <td className="text-ink-muted">{r.interface ?? "—"}</td>
                  <td className="text-ink-muted">{r.code}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Block>
      )}

      {/* ---- PIM ---- */}
      <Block
        title="Multicast routing (PIM)"
        badge={pim?.enabled ? <StatusPill status="healthy">enabled</StatusPill> : undefined}
      >
        {!pim || pim.interfaces.length === 0 ? (
          <p className="text-xs text-ink-muted">No PIM interfaces reported.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {pim.interfaces.map((i) => (
              <span
                key={i.vlan}
                className={`rounded border px-1.5 py-px font-mono text-xs ${
                  i.active ? "border-line text-ink" : "border-line text-ink-subtle"
                }`}
                title={i.ip ?? undefined}
              >
                vlan {i.vlan} · {i.active ? i.mode : "not running"}
              </span>
            ))}
          </div>
        )}
      </Block>

      {/* ---- what is actually flowing ---- */}
      {mroute?.supported && (
        <Block title="Multicast streams" count={mroute.entries.length}>
          {mroute.entries.length === 0 ? (
            <p className="text-xs text-ink-muted">
              Forwarding is {mroute.forwarding ? "on" : "off"}; no active streams right now.
            </p>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="text-ink-subtle">
                <tr className="[&>th]:py-1 [&>th]:pr-4 [&>th]:font-medium">
                  <th>Source</th>
                  <th>Group</th>
                  <th>In</th>
                  <th>Out</th>
                </tr>
              </thead>
              <tbody>
                {mroute.entries.map((e, idx) => (
                  <tr key={`${e.source}-${e.group}-${idx}`} className="[&>td]:py-1 [&>td]:pr-4">
                    {/* "*" is the shared-tree notation an operator reading a PIM table
                        expects; rewriting it would make the table unreadable to them. */}
                    <td className="font-mono text-ink-muted">{e.source}</td>
                    <td className="font-mono text-ink">{e.group}</td>
                    <td className="text-ink-muted">
                      {e.incoming_vlan != null ? `vlan ${e.incoming_vlan}` : "—"}
                    </td>
                    <td className="text-ink-muted">
                      {e.outgoing.length === 0
                        ? "—"
                        : e.outgoing.map((o) => (
                            <span
                              key={o.vlan}
                              className={`mr-1.5 ${
                                o.state === "forwarding" ? "text-healthy" : "text-ink-subtle"
                              }`}
                            >
                              vlan {o.vlan}
                            </span>
                          ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Block>
      )}
    </div>
  );
}

/** A titled sub-block inside a switch card. */
function Block({
  title,
  count,
  badge,
  children,
}: {
  title: string;
  count?: number;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-1 flex items-center gap-2">
        <span className="text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-subtle">
          {title}
        </span>
        {count != null && <span className="text-[0.6875rem] text-ink-subtle">{count}</span>}
        {badge}
      </div>
      {children}
    </div>
  );
}

function fmtBool(v: boolean | null | undefined): string {
  if (v == null) return "—";
  return v ? "on" : "off";
}

/* ---------------------------------------------------------------------------------------------
 * small shared bits
 * ------------------------------------------------------------------------------------------- */
function UnverifiedBanner() {
  return (
    <div className="rounded border border-line bg-surface-2 px-3 py-2 text-xs text-ink-muted">
      <span className="font-medium text-ink">Unverified syntax.</span> These IGMP-snooping commands
      have not been run on this hardware yet. Test on a spare VLAN first — a switch that refuses a
      line stops there and saves nothing.
    </div>
  );
}

function ItemList({
  tone,
  title,
  items,
}: {
  tone: "critical" | "warn";
  title?: string;
  items: { code: string; message: string }[];
}) {
  return (
    <div>
      {title && (
        <div className="mb-1 flex items-center gap-2">
          <StatusPill status={tone}>{title}</StatusPill>
        </div>
      )}
      <ul className="ml-1 flex flex-col gap-1 text-xs text-ink-muted">
        {items.map((it, i) => (
          <li key={`${it.code}-${i}`}>• {it.message}</li>
        ))}
      </ul>
    </div>
  );
}
